/* ==========================================================================
   Neopot motion engine.
   Drives: scroll reveals, sticky header, count-up stats, film crossfade,
   horizontal category rail. Every branch below has markup that uses it.
   Safe on every page: each feature no-ops when its markup is absent.
   ========================================================================== */

(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var seen = new WeakSet();

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      e.target.classList.add('np-in');
      io.unobserve(e.target);
      if (e.target.hasAttribute('data-np-count')) countUp(e.target);
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

  function scan() {
    // A pinned scene must never receive a transform: it would re-anchor its
    // position: sticky child and the pin would stop working.
    document.querySelectorAll('[data-np-reveal]').forEach(function (el, i) {
      if (seen.has(el)) return;
      seen.add(el);
      if (!el.classList.contains('np-pin-scene')) {
        el.classList.add('np-reveal');
        el.style.transitionDelay = Math.min(i, 5) * 45 + 'ms';
        io.observe(el);
      }
    });

    document.querySelectorAll('[data-np-count]').forEach(function (el) {
      if (seen.has(el)) return;
      seen.add(el);
      io.observe(el);
    });
  }

  function countUp(el) {
    var to = parseFloat(el.getAttribute('data-np-count'));
    if (isNaN(to)) return;
    var dec = to % 1 !== 0;
    var suffix = el.getAttribute('data-np-suffix') || '';
    if (REDUCED) {
      el.textContent = (dec ? to.toFixed(1) : to.toLocaleString('en-IN')) + suffix;
      return;
    }
    var t0 = performance.now();
    function tick(now) {
      var k = Math.min(1, (now - t0) / 1100);
      var v = to * (1 - Math.pow(1 - k, 3));
      el.textContent = (dec ? v.toFixed(1) : Math.round(v).toLocaleString('en-IN')) + suffix;
      if (k < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  function clamp(v) { return Math.min(1, Math.max(0, v)); }

  function progress(scene) {
    var sticky = scene.querySelector('.np-pin-sticky');
    if (!sticky) return 0;
    var range = scene.offsetHeight - sticky.offsetHeight;
    if (range <= 0) return 0;
    return clamp(-scene.getBoundingClientRect().top / range);
  }

  /* --------------------------------------------------- film crossfade layers */

  function driveFades() {
    document.querySelectorAll('.np-fade-scene').forEach(function (scene) {
      var layers = scene.querySelectorAll('.np-fade-layer');
      if (layers.length < 2) {
        if (layers.length === 1) layers[0].style.opacity = 1;
        return;
      }
      var pr = progress(scene);
      layers.forEach(function (l, i) {
        l.style.opacity = Math.max(0, 1 - Math.abs(pr * (layers.length - 1) - i) * 1.6);
      });
    });
  }

  /* ------------------------------------------------- horizontal category rail
     The rail used to be written once per scroll event, so it stepped by
     whatever distance the wheel or finger reported: visibly sudden. It now
     runs on its own frame loop and eases toward the scroll-derived target, so
     motion is continuous however coarse the input events are. */

  var rails = [];
  var ticking = false;

  function collectRails() {
    rails = [];
    document.querySelectorAll('.np-hscroll-scene').forEach(function (scene) {
      var sticky = scene.querySelector('.np-pin-sticky');
      var row = scene.querySelector('.np-hscroll-row');
      if (!sticky || !row) return;
      rails.push({
        scene: scene,
        sticky: sticky,
        row: row,
        cards: [].slice.call(row.querySelectorAll('.np-hcard')),
        cur: 0,
        target: 0,
        max: 0,
        primed: false
      });
    });
    measureRails();
  }

  function measureRails() {
    rails.forEach(function (r) {
      r.max = Math.max(0, r.row.scrollWidth - r.sticky.clientWidth);
      // Nothing to travel: collapse the dead scroll band and reset.
      if (r.max === 0) {
        r.scene.style.height = r.sticky.offsetHeight + 'px';
        r.row.style.transform = '';
        r.cards.forEach(function (c) { c.style.transform = ''; });
      } else {
        r.scene.style.height = '';
      }
    });
  }

  function railTick() {
    var alive = false;

    for (var i = 0; i < rails.length; i++) {
      var r = rails[i];
      if (r.max === 0) continue;

      var box = r.scene.getBoundingClientRect();
      var near = box.bottom > -400 && box.top < window.innerHeight + 400;
      if (!near) continue;

      r.target = progress(r.scene) * r.max;

      // Snap on the first frame so the rail never eases in from zero when the
      // visitor lands mid-scene (deep link, reload, back navigation).
      if (!r.primed) {
        r.cur = r.target;
        r.primed = true;
      } else {
        var d = r.target - r.cur;
        if (Math.abs(d) < 0.25) {
          r.cur = r.target;
        } else {
          r.cur += d * 0.14;
          alive = true;
        }
      }

      r.row.style.transform = 'translate3d(' + -r.cur + 'px, 0, 0)';

      // Zoom the card nearest the centre. Read all geometry, then write.
      var sr = r.sticky.getBoundingClientRect();
      var cx = sr.left + sr.width / 2;
      var half = sr.width / 2;
      var k = [];
      for (var c = 0; c < r.cards.length; c++) {
        var cr = r.cards[c].getBoundingClientRect();
        k.push(Math.max(0, 1 - Math.abs(cr.left + cr.width / 2 - cx) / half));
      }
      for (var c2 = 0; c2 < r.cards.length; c2++) {
        r.cards[c2].style.transform = 'scale(' + (1 + 0.07 * k[c2]) + ')';
      }
    }

    if (alive) {
      requestAnimationFrame(railTick);
    } else {
      ticking = false;
    }
  }

  function wakeRails() {
    if (REDUCED || ticking) return;
    ticking = true;
    requestAnimationFrame(railTick);
  }

  /* ------------------------------------------------------------ scroll driver
     Called directly, never behind a sticky requestAnimationFrame flag. The old
     flag pattern latched whenever a frame was dropped (backgrounded tab, theme
     editor re-render) and froze every pinned scene on its first layer. */

  var lastDrive = 0;
  function onScroll() {
    var on = window.scrollY > 40;
    document.querySelectorAll('.np-header').forEach(function (h) {
      h.classList.toggle('np-scrolled', on);
    });
    if (REDUCED) return;
    var now = Date.now();
    if (now - lastDrive >= 16) {
      lastDrive = now;
      try { driveFades(); } catch (e) {}
    }
    wakeRails();
  }

  function prime() {
    lastDrive = 0;
    measureRails();
    rails.forEach(function (r) { r.primed = false; });
    onScroll();
  }

  function boot() {
    scan();
    collectRails();
    prime();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  document.addEventListener('scroll', onScroll, { passive: true, capture: true });
  window.addEventListener('resize', prime, { passive: true });

  // Prime from several sources: one dropped frame must not leave the scenes
  // unpainted on their first layer.
  prime();
  setTimeout(prime, 0);
  setTimeout(prime, 220);
  requestAnimationFrame(prime);
  window.addEventListener('load', prime);

  // Shopify re-renders sections in the theme editor and on cart updates.
  new MutationObserver(function () { scan(); }).observe(document.documentElement, {
    childList: true,
    subtree: true
  });
  document.addEventListener('shopify:section:load', boot);
})();
